import { useStores } from '@src/Common/Hooks/useStores';
import ButtonDefault from '@src/Components/Atoms/ButtonDefault/ButtonDefault';
import CustomLoading from '@src/Components/Atoms/CustomLoading/CustomLoading';
import DropdownAnomalyRating from '@src/Components/Atoms/DropdownAnomalyRating/DropdownAnomalyRating';
import DropdownStatus from '@src/Components/Atoms/DropdownStatus/DropdownStatus';
import SkeletonDetailEventItem from '@src/Components/Atoms/Skeleton/SkeletonDetailEventItem/SkeletonDetailEventItem';
import { ModalCloseIcon } from '@src/Components/Icons/ModalCloseIcon/ModalCloseIcon';
import DetailEventItem from '@src/Components/Molecules/DetailEventItem/DetailEventItem';
import DropdownCountry from '@src/Components/Molecules/DropdownCountry/DropdownCountry';
import DefaultModal from '@src/Components/Molecules/Modal/DefaultModal/DefaultModal';
import { TEventItem } from '@src/Domain/MySubmission/MySubmission.interface';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef } from 'react';
import styles from './MySubmissionsDialog.module.scss';
import useMySubmissionsDialog from './useMySubmissionsDialog';

const LoadingPrevious: React.FC<{ isLoading: boolean }> = ({ isLoading }) => {
  if (isLoading)
    return (
      <div className={styles['LoadingPreviousContainer']}>
        <CustomLoading />
      </div>
    );
  return null;
};

const EventListData: React.FC<{
  isLoading: boolean;
  events: TEventItem[];
  onSubmitEvent: () => void;
}> = ({ isLoading, events, onSubmitEvent }) => {
  const { MySubmissionStore, DUMEventStore } = useStores();

  if (isLoading) {
    return (
      <div className={styles['LoadingContainer']}>
        {Array.from({ length: 5 }, (_v, k) => (
          <SkeletonDetailEventItem key={k} />
        ))}
      </div>
    );
  }

  if (events.length > 0) {
    return events.map((eventData, idx) => {
      return (
        <DetailEventItem
          key={eventData.id}
          id={idx}
          latlon={[eventData.lan, eventData.lat]}
          investigation={eventData.eventName}
          date={eventData.eventDate}
          locationName={eventData.city}
          status={eventData.status}
          type={eventData.type}
          onClick={() => {
            if (eventData.status === 'approved') {
              const rawData = MySubmissionStore.rawEvents.find(
                (v) => v.id === eventData.id
              );
              if (rawData) {
                DUMEventStore.selectedDetailDumGlobalIdIndex = 0;
                MySubmissionStore.onShowDetail(rawData);
              }
            }
          }}
        />
      );
    });
  }

  return (
    <div className={styles['EmptyContainer']}>
      <p className={styles['TextEmpty']}>You don’t have any submission yet.</p>
      <ButtonDefault label="Submit Event" onClick={onSubmitEvent} />
    </div>
  );
};
interface IMySubmissionsDialog {
  show: boolean;
  onClose: () => void;
  onSubmitEvent: () => void;
}

const MySubmissionsDialog: React.FC<IMySubmissionsDialog> = ({
  show,
  onClose,
  onSubmitEvent
}) => {
  const { MySubmissionStore } = useStores();
  useMySubmissionsDialog();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;

    if (container) {
      // Calculate the scroll position
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const spaceToScroll = 20;
      // Check if the user has reached the bottom of the container
      if (
        scrollTop + clientHeight > scrollHeight - spaceToScroll &&
        !MySubmissionStore.statusPreviousEvent.isLoading
      ) {
        // check if has previousId
        if (MySubmissionStore.canLoadMoreEvents) {
          MySubmissionStore.loadPreviousEvent();
        }
      }
    }
  }, [MySubmissionStore]);

  useEffect(() => {
    const container = containerRef.current;

    // Attach the scroll event listener to the container
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    // Cleanup the event listener on component unmount
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  return (
    <>
      <DefaultModal
        show={show}
        isBackgroundContainers={true}
        backgroundContent="transparent"
      >
        <section data-testid="my-submission" className={styles['Container']}>
          <header className={styles['Header']}>
            <h4>My Submission</h4>
            <button onClick={onClose}>
              <ModalCloseIcon />
            </button>
          </header>
          <main className={styles['Content']}>
            <div className={styles['Filter']}>
              <DropdownCountry
                disabled={MySubmissionStore.status.isLoading}
                defaultValue={MySubmissionStore.filterData.countries}
                onChange={(e) => {
                  MySubmissionStore.handleSelectField('countries', e);
                }}
              />
              <DropdownAnomalyRating
                disabled={MySubmissionStore.status.isLoading}
                defaultValue={
                  MySubmissionStore.filterData.anomalyRating
                    ?.find((v) => v > 0)
                    ?.toString() ?? 'all'
                }
                onChange={(e) => {
                  const anomalyList = Array.from({ length: 7 }, (_v, k) => {
                    return k === Number(e) - 1 ? Number(e) : undefined;
                  });
                  MySubmissionStore.handleSelectField(
                    'anomalyRating',
                    e === 'all' ? undefined : anomalyList
                  );
                }}
              />
              <DropdownStatus
                disabled={MySubmissionStore.status.isLoading}
                showUncovered={true}
                onChange={(e) => {
                  MySubmissionStore.handleSelectField('status', e);
                }}
              />
            </div>
            <div
              className={
                styles['ContainerEventList'] +
                ' ' +
                (MySubmissionStore.status.isLoading ? styles['isLoading'] : '')
              }
              ref={containerRef}
            >
              <EventListData
                isLoading={MySubmissionStore.status.isLoading}
                events={MySubmissionStore.events}
                onSubmitEvent={onSubmitEvent}
              />
            </div>
            <LoadingPrevious
              isLoading={MySubmissionStore.statusPreviousEvent.isLoading}
            />
          </main>
        </section>
      </DefaultModal>
    </>
  );
};

export default observer(MySubmissionsDialog);
